import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ApiError,
  OrganizationPublicResponse,
  OrganizationWebPagePublicResponse,
  OrganizationsService,
  TutorPublicResponse,
  TutorWebPageResponse,
  TutorsService,
  WebPageService,
} from "client/openapi";
import { LoadingBlock } from "components/StatusBlock";
import PublicTutorPage from "components/PublicBooking/PublicTutorPage";
import { PublicMessage } from "components/PublicBooking/PublicShell";
import { orgHref } from "components/PublicBooking/publicBooking";
import { isWhiteLabelHost } from "util/whiteLabel";
import { PageStatus } from "types";

/** /tutors/:id, or /:tutorSlug on an org's own domain. */
function TutorWebPage() {
  const { id, tutorSlug } = useParams<{ id?: string; tutorSlug?: string }>();
  const [tutorPage, setTutorPage] = useState<TutorWebPageResponse>();
  const [tutor, setTutor] = useState<TutorPublicResponse>();
  const [page, setPage] = useState<OrganizationWebPagePublicResponse>();
  const [org, setOrg] = useState<OrganizationPublicResponse>();
  const [failed, setFailed] = useState<number>();
  // A hidden tutor's org, to link to its page.
  const [orgId, setOrgId] = useState<number>();

  useEffect(() => {
    const loadTutorPage = id
      ? WebPageService.getTutorWebPage({ tutorId: Number(id) })
      : WebPageService.getTutorWebPageBySlug({ tutorSlug: tutorSlug ?? "" });
    loadTutorPage
      .then((loaded) => {
        setTutorPage(loaded);
        return TutorsService.getTutorForWebPage({ tutorId: loaded.tutor_id });
      })
      .then((loaded) => {
        setTutor(loaded);
        return Promise.all([
          WebPageService.getOrganizationWebPage({ orgId: loaded.org_id }),
          OrganizationsService.getOrganizationForWebPage({
            orgId: loaded.org_id,
          }),
        ]);
      })
      .then(([loadedPage, loadedOrg]) => {
        setPage(loadedPage);
        setOrg(loadedOrg);
      })
      .catch((e: ApiError) => {
        setOrgId(e.body?.detail?.org_id);
        setFailed(e.status ?? 500);
      });
  }, [id, tutorSlug]);

  if (failed === 404)
    return (
      <PublicMessage title="This tutor isn't taking bookings online.">
        {(orgId || isWhiteLabelHost()) && (
          <Link
            className="font-semibold underline"
            to={orgId ? orgHref(orgId) : "/"}
          >
            See who is
          </Link>
        )}
      </PublicMessage>
    );
  if (failed)
    return (
      <PublicMessage title="This page didn't load.">
        Check your connection and try again.
      </PublicMessage>
    );
  if (!tutorPage || !tutor || !page || !org)
    return <LoadingBlock status={PageStatus.LOADING} />;

  return (
    <>
      <Helmet>
        <title>{`${tutor.first_name} ${tutor.last_name} · ${org.name}`}</title>
      </Helmet>
      <PublicTutorPage
        page={page}
        org={org}
        tutor={tutor}
        tutorPage={tutorPage}
      />
    </>
  );
}

export default TutorWebPage;
